import React, { useState } from 'react';
import { Container, Row, Col, Form, Card, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaPaperPlane, FaCheckCircle, FaExclamationCircle, FaClock, FaQuestionCircle, FaLifeRing, FaCopy } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const SUPPORT_EMAIL = 'admin@mileageriot.com';
const DISCORD_INVITE = 'https://discord.gg/nQSQmJbXN3';

const Support = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const buildBody = () => [
    `${t('support.form.name')}: ${formData.name}`,
    `${t('support.form.email')}: ${formData.email}`,
    '',
    formData.message
  ].join('\n');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${formData.subject}\n\n${buildBody()}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 4000);
    } catch (err) {
      console.error('Clipboard unavailable:', err);
    }
  };

  /**
   * This used to build an `emailData` object, throw it away, wait 2 seconds and
   * report success — no request was ever made, so every message users sent was
   * silently discarded. There is no contact endpoint on the Hub API yet, so the
   * submission is handed to the visitor's mail client instead. That actually
   * delivers, and the copy button covers browsers with no mail handler.
   */
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowAlert(false);

    try {
      const subject = `[Mileage Riot] ${formData.subject}`;
      const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(buildBody())}`;
      window.location.href = mailto;

      setSubmitStatus('success');
      setShowAlert(true);
    } catch (error) {
      console.error('Error opening mail client:', error);
      setSubmitStatus('error');
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = formData.name && formData.email && formData.subject && formData.message;

  return (
    <>
    <Helmet>
      <title>Поддержка — Mileage Riot ВТК | ETS2 & ATS</title>
      <meta name="description" content="Поддержка Mileage Riot — напишите нам на почту или в Discord. Отвечаем в течение 24-48 часов в рабочие дни." />
      <link rel="canonical" href="https://mileageriot.com/support" />
      <meta property="og:title" content="Поддержка — Mileage Riot" />
      <meta property="og:url" content="https://mileageriot.com/support" />
      <meta property="og:locale" content="ru_RU" />
    </Helmet>
    <div className="support-page">
      <Container className="py-5 mt-3">
        <Row className="justify-content-center">
          <Col lg={10}>
            <div className="text-center mb-5">
              <div className="support-header-icon mb-4">
                <FaLifeRing className="support-main-icon" />
              </div>
              <h1 className="display-4 fw-bold text-white mb-3">
                {t('support.title')}
              </h1>
              <p className="lead text-light">
                {t('support.subtitle')}
              </p>
            </div>

            {/* Support Options Grid */}
            <Row className="mb-5">
              <Col md={4} className="mb-4">
                <Card className="support-option-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="support-option-icon mb-3">
                      <FaEnvelope />
                    </div>
                    {/* Was <h5> directly under the page <h1>, which jumped two
                        levels and put these cards below the form's <h3>. */}
                    <h2 className="support-option-title text-white mb-2">
                      {t('support.contact.title')}
                    </h2>
                    <p className="text-light mb-3 small">
                      {t('support.contact.description')}
                    </p>
                    <a href={`mailto:${SUPPORT_EMAIL}`} className="btn btn-support-outline">
                      {SUPPORT_EMAIL}
                    </a>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4} className="mb-4">
                <Card className="support-option-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="support-option-icon mb-3">
                      <FaClock />
                    </div>
                    <h2 className="support-option-title text-white mb-2">{t('support.responseTime')}</h2>
                    <p className="text-light mb-3 small">
                      {t('support.response_time')}
                    </p>
                    <span className="support-badge">{t('support.responseHours')}</span>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4} className="mb-4">
                <Card className="support-option-card h-100">
                  <Card.Body className="text-center p-4">
                    <div className="support-option-icon mb-3">
                      <FaQuestionCircle />
                    </div>
                    <h2 className="support-option-title text-white mb-2">{t('support.faq.title')}</h2>
                    <p className="text-light mb-3 small">
                      {t('support.faq.description')}
                    </p>
                    <a
                      href={DISCORD_INVITE}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-support-outline"
                    >
                      {t('support.faq.discord')}
                    </a>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Alert for form submission */}
            <div aria-live="polite">
              {showAlert && (
                <Alert
                  variant={submitStatus === 'success' ? 'success' : 'danger'}
                  dismissible
                  onClose={() => setShowAlert(false)}
                  className="mb-4 support-alert"
                >
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      {submitStatus === 'success' ? (
                        <FaCheckCircle className="fs-4" />
                      ) : (
                        <FaExclamationCircle className="fs-4" />
                      )}
                    </div>
                    <div>
                      <Alert.Heading className="h5 mb-1">
                        {submitStatus === 'success'
                          ? t('support.form.handoff.title', 'Открываем почтовый клиент')
                          : t('support.form.error.title')
                        }
                      </Alert.Heading>
                      <p className="mb-0 small">
                        {submitStatus === 'success'
                          ? t('support.form.handoff.message', 'Письмо уже заполнено — остаётся нажать «Отправить». Если клиент не открылся, скопируйте текст кнопкой ниже и пришлите его нам на почту или в Discord.')
                          : t('support.form.error.message')
                        }
                      </p>
                    </div>
                  </div>
                </Alert>
              )}
            </div>

            {/* Support Form Card */}
            <Card className="support-form-card shadow-lg">
              <Card.Header className="support-form-header text-white">
                <div className="d-flex align-items-center">
                  <FaPaperPlane className="me-3 fs-4" />
                  <div>
                    <h2 className="h4 mb-0">
                      {t('support.form.title')}
                    </h2>
                    <small className="opacity-75">
                      {t('support.form.subtitleMail', 'Заполните форму — письмо откроется в вашем почтовом клиенте уже готовым')}
                    </small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4 support-form-body">
                <Form onSubmit={handleSubmit} className="support-form">
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4" controlId="supportName">
                        <Form.Label className="support-form-label">
                          {t('support.form.name')} <span className="text-danger">*</span>
                        </Form.Label>
                        {/* controlId wires label->input; before this none of the
                            four fields had an id, so no label was associated. */}
                        <Form.Control
                          type="text"
                          name="name"
                          autoComplete="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          placeholder={t('support.form.name_placeholder')}
                          className="support-form-control"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-4" controlId="supportEmail">
                        <Form.Label className="support-form-label">
                          {t('support.form.email')} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          autoComplete="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          placeholder={t('support.form.email_placeholder')}
                          className="support-form-control"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4" controlId="supportSubject">
                    <Form.Label className="support-form-label">
                      {t('support.form.subject')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder={t('support.form.subject_placeholder')}
                      className="support-form-control"
                    />
                  </Form.Group>

                  <Form.Group className="mb-4" controlId="supportMessage">
                    <Form.Label className="support-form-label">
                      {t('support.form.message')} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder={t('support.form.message_placeholder')}
                      className="support-form-control"
                    />
                  </Form.Group>

                  <div className="support-form-actions">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-hero-primary support-submit"
                    >
                      <FaPaperPlane />
                      <span>{t('support.form.submit')}</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleCopy}
                      disabled={!canSubmit}
                      className="btn btn-hero-secondary"
                    >
                      <FaCopy />
                      <span>
                        {copied
                          ? t('support.form.copied', 'Скопировано')
                          : t('support.form.copy', 'Скопировать текст')}
                      </span>
                    </button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Support Footer */}
            <div className="support-footer text-center mt-5">
              <div className="support-footer-content">
                <FaClock className="support-footer-icon mb-2" />
                <p className="text-light mb-0">
                  {t('support.response_time')}
                </p>
                <small className="support-footer-note">
                  {t('support.urgentContact')}
                </small>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
    </>
  );
};

export default Support;
